import { ActiveTrack, AudioTrack, DecodedTrack } from "../../../config/audio/types";
import { resolveMediaURL } from "../../loaders";
import { audioClamp } from "./audioCalculations";

/**
 * Decode a single track
 */
export const decodeTrack = async (
	track: AudioTrack,
	audioContext: AudioContext,
): Promise<DecodedTrack> => {
	try {
		const url = resolveMediaURL(track.source);
		const response = await fetch(url);
		const buffer = await response.arrayBuffer();
		const audioBuffer = await audioContext.decodeAudioData(buffer);
		return { ...track, data: audioBuffer };
	} catch (err) {
		throw new Error(`Failed to decode track ${track.source}: ${String(err)}`);
	}
};

/**
 * Decode multiple tracks in parallel
 */
export const decodeTracks = async (
	tracks: readonly AudioTrack[],
	audioContext: AudioContext,
): Promise<readonly DecodedTrack[]> => {
	return Promise.all(tracks.map((track) => decodeTrack(track, audioContext)));
};

export function webAudioTrackStop(track: ActiveTrack) {
	try {
		track.sourceNode.stop();
	} catch {
	} finally {
		track.sourceNode.onended = null;
		track.gainNode.disconnect();
	}
}

export function webAudioGainSet(track: ActiveTrack, gain: number) {
	track.gainNode.gain.value = audioClamp(gain);
}

export function webAudioTrackStart(track: ActiveTrack) {
	track.sourceNode.start();
}

/**
 * Create audio node from decoded track (pure except for Web Audio API calls)
 */
export const webAudioNodeCreate = (
	track: DecodedTrack,
	audioContext: AudioContext,
	playlistGain: number,
): ActiveTrack => {
	const sourceNode = audioContext.createBufferSource();
	const gainNode = audioContext.createGain();
	gainNode.gain.value = track.defaultGain * playlistGain;
	sourceNode.buffer = track.data;
	sourceNode.connect(gainNode).connect(audioContext.destination);
	return { ...track, sourceNode, gainNode };
};
/**
 * Create audio nodes from decoded tracks (functional composition)
 */
export const webAudioNodesCreate = (
	tracks: readonly DecodedTrack[],
	audioContext: AudioContext,
	playlistGain: number,
): readonly ActiveTrack[] =>
	tracks.map((track) => webAudioNodeCreate(track, audioContext, playlistGain));
